import React from 'react';
import { useSelector } from 'react-redux';
import { ArrayHelpers, Copy, Loading, SystemText } from 'e4shared';
import Filters from '../_filters';
import Chart from './chart';
import GroupPicker from 'components/admin/forms/groupPicker';
import WidgetBase, { groupSet, widgetLoad, widgetLoadRecommendations } from '../_base';
import BlobImage from 'components/blobImage';
import { withRouter } from 'react-router-dom';

/*
Only change method name, system text, response mapping, validator and filter fields when copying
*/

export const TeamCompetencyRatings = ({ defaultProps, id, item, setItem, onDelete, history }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const zero = user.companySettings.zero;
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});
    const [keys, setKeys] = React.useState([]);

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    const load = () => {
        const override = response => {
            setData(
                response.data.map(r => {
                    const set = { id: r.name };
                    r.data.forEach(d => {
                        set[d.x] = d.y.toFixed(1);
                    });
                    return set;
                })
            );

            setRecommendationData(response.recommendation);
            ArrayHelpers.existsAndHasLength(response.data) && setKeys(response.data[0].data.map(d => d.x));
        };

        widgetLoad(
            item.id,
            user.guid,
            validator,
            setLoading,
            setNotConfigured,
            setData,
            () => {
                widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
            },
            override
        );
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;
        setValues(temp);
    };

    const height = defaultProps.height(item);

    const defaults = { show_For: true, period: 'M' };

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);
        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Edit/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = SystemText('Dashboard/Widgets/TeamRatings/Fields/Group/Error');
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const chart = <Chart data={data} item={item} keys={keys} />;

    const missing = missing => {
        if (!ArrayHelpers.existsAndHasLength(missing)) return null;
        const managers = missing.reduce((a, c) => {
            const curr = a.find(x => x.manager_Id === c.manager_Id);
            if (curr) {
                curr.count = curr.count + 1;
                return a;
            } else {
                return a.concat([{ ...c, count: 1 }]);
            }
        }, []);

        return (
            <div>
                <p>{SystemText('Dashboard/Widgets/TeamRatings/Recommends/Missing/Intro')}:</p>
                <ul>
                    {managers
                        .sort((a, b) => (a.manager_List_Name > b.manager_List_Name ? 1 : -1))
                        .map(m => (
                            <li
                                key={`missing-${m.manager_Id}`}
                                className="flex flex-center list-image pointer"
                                title={SystemText('Dashboard/Widgets/TeamRatings/Recommends/Team', m.manager_First_Name)}
                                onClick={() => history.push(`search?manager=${m.manager_Id}&name=${m.manager_Full_Name}`)}
                            >
                                <BlobImage folder="avatar" src={m.avatar} initials={m.initials} className="img-circle" />
                                <span className="ml5">
                                    {m.manager_Full_Name} -{' '}
                                    {SystemText('Dashboard/Widgets/TeamRatings/Recommends/Missing/Unrated', m.count)}
                                </span>
                            </li>
                        ))}
                </ul>
            </div>
        );
    };

    const wild = wild => {
        if (!ArrayHelpers.existsAndHasLength(wild) || zero) return null;

        return (
            <div>
                <p>{SystemText('Dashboard/Widgets/TeamRatings/Recommends/Wild/Intro')}:</p>
                <ul>
                    {wild
                        .sort((a, b) => (a.list_Name > b.list_Name ? 1 : -1))
                        .map((m, i) => (
                            <li
                                key={`wild-${m.id}-${i}`}
                                className="flex flex-center list-image pointer"
                                title={SystemText('Dashboard/Widgets/TeamRatings/Recommends/Team', m.first_Name)}
                                onClick={() => history.push(`search?manager=${m.id}&name=${m.full_Name}`)}
                            >
                                <BlobImage folder="avatar" src={m.avatar} initials={m.initials} className="img-circle" />
                                <span className="ml5">
                                    {m.full_Name} -{' '}
                                    {SystemText(
                                        `Dashboard/Widgets/TeamRatings/Recommends/Wild/${
                                            m.value === 'staff' ? 'Team' : 'Self'
                                        }/${m.active ? 'High' : 'Low'}`
                                    )}
                                </span>
                            </li>
                        ))}
                </ul>
            </div>
        );
    };

    const worst = worst => {
        if (!ArrayHelpers.existsAndHasLength(worst)) return null;
        return <Copy value={SystemText('Dashboard/Widgets/TeamRatings/Recommends/Worst/Intro', `<b>${worst}</b>`)} />;
    };

    const recommendations = recLoading ? (
        <Loading />
    ) : (
        [missing(recommendationData.missing), wild(recommendationData.wild), worst(recommendationData.worst)].filter(x => x)
    );

    const filters = (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                label={SystemText('Dashboard/Widgets/TeamRatings/Fields/Group/Label')}
                help={SystemText('Dashboard/Widgets/TeamRatings/Fields/Group/Help')}
                mandatory
                allowMultiple
                managersOnly
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                showUserOption={defaultProps.company}
                controlId={id}
            />
        </Filters>
    );

    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={null}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default withRouter(TeamCompetencyRatings);
